import React from 'react';
import Icon from '../../../components/AppIcon';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const CampaignAnalytics = ({ analytics }) => {
  const COLORS = ['#6366F1', '#8B5CF6', '#F59E0B', '#10B981'];

  const StatCard = ({ icon, label, value, change, trend }) => (
    <div className="bg-card border border-border rounded-lg p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
          <Icon name={icon} size={20} color="var(--color-primary)" />
        </div>
        {change && (
          <div className={`flex items-center gap-1 text-xs font-medium ${trend === 'up' ? 'text-success' : 'text-error'}`}>
            <Icon name={trend === 'up' ? 'TrendingUp' : 'TrendingDown'} size={14} />
            <span>{change}</span>
          </div>
        )}
      </div>
      <p className="text-2xl font-bold text-foreground mb-1">{value}</p>
      <p className="text-sm text-muted-foreground">{label}</p>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon="Eye"
          label="Toplam Erişim"
          value={analytics?.totalReach}
          change="+12.5%"
          trend="up"
        />
        <StatCard
          icon="Heart"
          label="Etkileşim Oranı"
          value={analytics?.engagementRate}
          change="+8.3%"
          trend="up"
        />
        <StatCard
          icon="TrendingUp"
          label="ROI"
          value={analytics?.roi}
          change="+15.2%"
          trend="up"
        />
        <StatCard
          icon="DollarSign"
          label="Toplam Harcama"
          value={analytics?.totalSpent}
          change="-5.1%"
          trend="down"
        />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-card border border-border rounded-lg p-6">
          <h3 className="font-semibold text-foreground mb-4">Haftalık Erişim Trendi</h3>
          <div className="w-full h-64" aria-label="Haftalık Erişim Grafiği">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={analytics?.weeklyReach}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis dataKey="day" stroke="var(--color-muted-foreground)" />
                <YAxis stroke="var(--color-muted-foreground)" />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'var(--color-popover)', 
                    border: '1px solid var(--color-border)',
                    borderRadius: '0.5rem'
                  }}
                />
                <Legend />
                <Line type="monotone" dataKey="reach" stroke="var(--color-primary)" strokeWidth={2} name="Erişim" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-6">
          <h3 className="font-semibold text-foreground mb-4">Platform Dağılımı</h3>
          <div className="w-full h-64" aria-label="Platform Dağılım Grafiği">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={analytics?.platformDistribution}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100)?.toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {analytics?.platformDistribution?.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS?.[index % COLORS?.length]} />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'var(--color-popover)', 
                    border: '1px solid var(--color-border)',
                    borderRadius: '0.5rem'
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-6 lg:col-span-2">
          <h3 className="font-semibold text-foreground mb-4">Kampanya Performansı</h3>
          <div className="w-full h-64" aria-label="Kampanya Performans Grafiği">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={analytics?.campaignPerformance}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis dataKey="campaign" stroke="var(--color-muted-foreground)" />
                <YAxis stroke="var(--color-muted-foreground)" />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'var(--color-popover)', 
                    border: '1px solid var(--color-border)',
                    borderRadius: '0.5rem'
                  }}
                />
                <Legend />
                <Bar dataKey="reach" fill="var(--color-primary)" name="Erişim" />
                <Bar dataKey="engagement" fill="var(--color-secondary)" name="Etkileşim" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CampaignAnalytics;